import React, { useState, useEffect, useCallback } from 'react';
import {
    DollarSign, TrendingUp, Mic, BarChart3, Sparkles,
    Loader2, AlertTriangle, Volume2, Users, Eye, MousePointer,
    CheckCircle, Zap, ArrowDownRight, Clock, Award, Beaker,
    Shield,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';

/**
 * MonetizationTab — Admin analytics for conversion, activation, retention,
 * experimentation, and winner signals.
 *
 * Phase 3A/3B: Client-side event filtering (fragile at scale)
 * Phase 4A: Backend aggregation via GET /internal/analytics/monetization
 */

const MonetizationTab = ({ filters = {} }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await adminAnalytics.getMonetization({
                days: filters.days || 7,
            });
            setData(res.data);
        } catch (err) {
            if (err.response?.status === 403) {
                setError('Access denied. Admin role required.');
            } else {
                setError(err.response?.data?.detail || 'Failed to load monetization data.');
            }
        } finally {
            setLoading(false);
        }
    }, [filters.days]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={centerStyle}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#f59e0b' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading monetization data…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={centerStyle}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#f59e0b', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Retry
                </button>
            </div>
        );
    }

    if (!data) return null;

    const uf = data.upgrade_funnel || {};
    const af = data.activation_funnel || {};
    const attr = data.attribution || {};
    const ret = data.retention || {};
    const exp = data.experiments || {};
    const winners = data.winner_signals || {};

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Monetization Intelligence</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    Last {data.period_days || 7} days · Conversion → Activation → Retention
                </p>
            </div>

            {/* ── Winner Signals ── */}
            {(winners.best_nudge_ctr || winners.best_upgrade_source || winners.best_experiment_variant) && (
                <Section title="Winner Signals" icon={Award} color="#10b981">
                    {winners.best_nudge_ctr && (
                        <Card
                            label="Best Nudge CTR"
                            value={winners.best_nudge_ctr.ctr_pct}
                            format="percent"
                            icon={TrendingUp}
                            accent="#10b981"
                            sub={`${winners.best_nudge_ctr.nudge_type} (${winners.best_nudge_ctr.shown} shown)`}
                        />
                    )}
                    {winners.best_upgrade_source && (
                        <Card
                            label="Top Upgrade Source"
                            value={winners.best_upgrade_source.count}
                            icon={DollarSign}
                            accent="#f59e0b"
                            sub={winners.best_upgrade_source.source}
                        />
                    )}
                    {winners.best_experiment_variant && (
                        <Card
                            label="Best Variant CTR"
                            value={winners.best_experiment_variant.ctr_pct}
                            format="percent"
                            icon={Beaker}
                            accent="#8b5cf6"
                            sub={`${winners.best_experiment_variant.experiment} / ${winners.best_experiment_variant.variant}`}
                        />
                    )}
                </Section>
            )}

            {/* ── Upgrade Funnel ── */}
            <Section title="Upgrade Funnel" icon={TrendingUp} color="#f59e0b">
                <Card label="Nudge Impressions" value={uf.nudge_shown ?? 0} icon={Eye} accent="#6b7280" />
                <Card label="Nudge Clicks" value={uf.nudge_clicked ?? 0} icon={MousePointer} accent="#3b82f6" />
                <Card label="Nudge CTR" value={uf.nudge_ctr_pct ?? 0} format="percent" icon={TrendingUp}
                    accent={(uf.nudge_ctr_pct ?? 0) > 5 ? '#10b981' : '#f59e0b'} />
                <Card label="Pricing Opens" value={uf.pricing_opened ?? 0} icon={DollarSign} accent="#f59e0b" />
                <Card label="Checkout Started" value={uf.checkout_started ?? 0} icon={DollarSign} accent="#8b5cf6" />
                <Card label="Upgrade Completed" value={uf.upgrade_completed ?? 0} icon={CheckCircle} accent="#10b981" />
                <Card label="Checkout → Upgrade" value={uf.checkout_to_upgrade_pct ?? 0} format="percent" icon={ArrowDownRight}
                    accent={(uf.checkout_to_upgrade_pct ?? 0) > 50 ? '#10b981' : '#f59e0b'} />
            </Section>

            {/* ── Activation Funnel ── */}
            <Section title="Post-Upgrade Activation" icon={Zap} color="#8b5cf6">
                <Card label="Activation Prompts" value={af.post_upgrade_prompt_shown ?? 0} icon={Sparkles} accent="#6b7280" />
                <Card label="Prompt Clicks" value={af.post_upgrade_prompt_clicked ?? 0} icon={MousePointer} accent="#3b82f6" />
                <Card label="First Enhanced Lesson" value={af.first_enhanced_lesson ?? 0} icon={Zap} accent="#f59e0b" />
                <Card label="First Premium Preset" value={af.first_premium_preset ?? 0} icon={Mic} accent="#8b5cf6" />
                <Card label="Activated ≤ 24h" value={af.activated_within_24h ?? 0} icon={Clock} accent="#10b981"
                    sub="Premium action within first day" />
                <Card label="Activated ≤ 7d" value={af.activated_within_7d ?? 0} icon={Clock} accent="#3b82f6"
                    sub="Premium action within first week" />
                <Card label="Unique Adopters" value={af.unique_adopters ?? 0} icon={Users} accent="#10b981"
                    sub="Users with any premium action" />
            </Section>

            {/* ── Paid Retention ── */}
            <Section title="Paid Retention" icon={Shield} color="#3b82f6">
                <Card label="Total Paid Users" value={ret.total_paid_users ?? 0} icon={Users} accent="#3b82f6" />
                <Card label="Active Last 24h" value={ret.paid_active_1d ?? 0} icon={Clock} accent="#10b981"
                    sub={`${ret.paid_retention_1d_pct ?? 0}% of paid`} />
                <Card label="Active Last 7d" value={ret.paid_active_7d ?? 0} icon={Clock} accent="#f59e0b"
                    sub={`${ret.paid_retention_7d_pct ?? 0}% of paid`} />
                <Card label="Active Last 30d" value={ret.paid_active_30d ?? 0} icon={Clock} accent="#8b5cf6"
                    sub={`${ret.paid_retention_30d_pct ?? 0}% of paid`} />
            </Section>

            {/* ── Attribution ── */}
            <Section title="Attribution" icon={Sparkles} color="#f59e0b">
                {(attr.nudge_performance || []).map(np => (
                    <Card key={np.nudge_type}
                        label={`${np.nudge_type} nudge`}
                        value={np.ctr_pct}
                        format="percent"
                        icon={np.ctr_pct > 5 ? TrendingUp : Eye}
                        accent={np.ctr_pct > 5 ? '#10b981' : '#6b7280'}
                        sub={`${np.shown} shown · ${np.clicked} clicked`}
                    />
                ))}
                {(attr.upgrades_by_source || []).map(us => (
                    <Card key={us.source}
                        label={`Upgrades: ${us.source}`}
                        value={us.count}
                        icon={CheckCircle}
                        accent="#f59e0b"
                    />
                ))}
            </Section>

            {/* ── Experiments ── */}
            {Object.keys(exp).length > 0 && (
                <Section title="Experiment Results" icon={Beaker} color="#8b5cf6">
                    {Object.entries(exp).map(([expName, variants]) =>
                        Object.entries(variants).map(([varKey, metrics]) => (
                            <Card key={`${expName}-${varKey}`}
                                label={`${expName} / ${varKey}`}
                                value={metrics.ctr_pct}
                                format="percent"
                                icon={Beaker}
                                accent={metrics.ctr_pct > 5 ? '#10b981' : '#6b7280'}
                                sub={`${metrics.shown} shown · ${metrics.clicked} clicked`}
                            />
                        ))
                    )}
                </Section>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};


// ── Shared layout components ──────────────────────────────────────────────

const centerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 300, flexDirection: 'column', gap: 10,
};

const Section = ({ title, icon: Icon, color = '#6366f1', children }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 12, paddingBottom: 8,
            borderBottom: `2px solid ${color}20`,
        }}>
            {Icon && <Icon size={15} style={{ color }} />}
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
        </div>
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 12,
        }}>
            {children}
        </div>
    </div>
);

const Card = ({ label, value, icon: Icon, accent = '#6366f1', format, sub }) => {
    const displayValue = format === 'percent'
        ? `${value}%`
        : typeof value === 'number'
            ? value.toLocaleString()
            : value;

    return (
        <div style={{
            background: '#fff', borderRadius: 12, padding: '16px 18px',
            border: '1px solid #e5e7eb', transition: 'box-shadow 0.15s',
            cursor: 'default',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>{label}</span>
                {Icon && <Icon size={14} style={{ color: accent }} />}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{displayValue}</div>
            {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
        </div>
    );
};


export default MonetizationTab;
